import type { getOneSenderOutput } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { getSendersDetails } from "@/services";
import { useSenderStatusTranslator } from "@/utils/translations";
import { toast } from "@st-marketplace/ui";
import { useCallback, useState } from "react";
import { usePapaParse } from "react-papaparse";

type SenderAuth = getOneSenderOutput["auth"];

/** One cell per auth kind, matching how the UI and the CSV import spell them. */
const renderAuthValue = (auth: SenderAuth): string => {
	switch (auth.type) {
		case "gauth":
			return auth.auth;
		case "backup_code":
			return auth.auth.join(", ");
		case "imap":
			return [
				`server=${auth.server}`,
				`port=${auth.port}`,
				`username=${auth.username}`,
				`password=${auth.password}`,
				`ssl=${auth.ssl}`,
			].join("; ");
		default:
			return "";
	}
};

export const useSendersCsvExport = () => {
	const { t } = useTranslation();
	const translateStatus = useSenderStatusTranslator(t);
	const { jsonToCSV } = usePapaParse();
	const [isExporting, setIsExporting] = useState(false);

	const exportSenders = useCallback(
		async (senderIds: string[]) => {
			if (!senderIds.length) return;

			setIsExporting(true);
			try {
				const rows = (await getSendersDetails(senderIds)).map((sender) => ({
					Email: sender.ea_email,
					Password: sender.ea_password,
					"Auth Type": sender.auth.type,
					"Auth Value": renderAuthValue(sender.auth),
					"Current Coin": String(sender.current_coin ?? 0),
					Status: String(translateStatus(sender.status).text),
				}));

				const blob = new Blob([jsonToCSV(rows, { header: true })], {
					type: "text/csv;charset=utf-8;",
				});
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = `senders-${new Date().toISOString().slice(0, 10)}.csv`;
				link.click();
				URL.revokeObjectURL(url);
			} catch {
				toast.error(t("sender.action.failedExportSenders"));
			} finally {
				setIsExporting(false);
			}
		},
		[jsonToCSV, translateStatus, t],
	);

	return { exportSenders, isExporting };
};
